/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import { SITE } from "@/data/site";

export default function LegalPage({ kind = "cookie" }) {
    const isCookie = kind === "cookie";
    return (
        <article
            className="pt-[120px] md:pt-[140px] pb-24 bg-ivory min-h-[80vh]"
            data-testid={isCookie ? "cookie-page" : "privacy-page"}
        >
            <div className="mx-auto max-w-[880px] px-6 md:px-10">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ink-muted hover:text-raspberry"
                >
                    <ArrowLeft size={14} />
                    Torna alla home
                </Link>
                <div className="mt-10">
                    <SectionLabel
                        text={isCookie ? "Cookie Policy" : "Privacy Policy"}
                    />
                </div>
                <h1 className="mt-6 font-display text-[36px] md:text-[52px] leading-[1.05] text-ink">
                    {isCookie
                        ? "Cookie Policy."
                        : "Privacy Policy."}
                </h1>
                <p className="mt-6 text-ink-muted text-sm">
                    Ultimo aggiornamento:{" "}
                    {new Date().toLocaleDateString("it-IT")}
                </p>

                <div className="mt-14 prose max-w-none text-ink-soft text-[16px] leading-[1.85] space-y-6">
                    {isCookie ? <CookieContent /> : <PrivacyContent />}
                </div>

                <div className="mt-16 rounded-[24px] bg-rose-powder/50 p-8">
                    <SectionLabel text="Titolare del trattamento" tone="muted" />
                    <p className="mt-3 font-display text-2xl text-ink">
                        Spazio Isa · Centro Estetico
                    </p>
                    <p className="mt-2 text-ink-soft text-[14.5px] leading-relaxed">
                        {SITE.address.street} — {SITE.address.cap}{" "}
                        {SITE.address.city} · {SITE.address.district}
                        <br />
                        Tel. {SITE.phone} · Cell. {SITE.mobile}
                    </p>
                </div>
            </div>
        </article>
    );
}

function CookieContent() {
    return (
        <>
            <h2 className="font-display text-3xl text-ink mt-10">
                1. Cosa sono i cookie
            </h2>
            <p>
                I cookie sono piccoli file di testo che i siti visitati inviano
                al terminale (computer, smartphone, tablet) dell'utente, dove
                vengono memorizzati per essere poi ritrasmessi agli stessi siti
                alla successiva visita.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                2. Cookie utilizzati da questo sito
            </h2>
            <p>
                Il sito <strong>Spazio Isa</strong> utilizza esclusivamente
                cookie tecnici strettamente necessari al corretto funzionamento
                della navigazione. Non utilizziamo cookie di profilazione, non
                tracciamo la navigazione degli utenti e non condividiamo dati
                con terze parti a fini pubblicitari.
            </p>
            <p>
                Un unico dato tecnico viene memorizzato nel{" "}
                <em>localStorage</em> del browser con la chiave{" "}
                <code className="text-raspberry">cookie_choice</code> allo scopo
                di ricordare la scelta espressa dall'utente sul banner cookie.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                3. Google Maps
            </h2>
            <p>
                All'interno della pagina Contatti è integrata una mappa Google
                Maps tramite embed pubblico. Google può raccogliere informazioni
                di navigazione secondo la propria informativa privacy. È
                possibile consultarla su{" "}
                <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-raspberry hover:underline"
                >
                    policies.google.com/privacy
                </a>
                .
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                4. Gestione dei cookie
            </h2>
            <p>
                L'utente può in qualsiasi momento modificare le proprie
                preferenze eliminando i dati locali del browser (Impostazioni →
                Privacy → Cancella dati di navigazione). Al successivo accesso
                verrà nuovamente mostrato il banner per esprimere una nuova
                preferenza.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                5. Contatti
            </h2>
            <p>
                Per qualsiasi domanda relativa all'uso dei cookie o al
                trattamento dei dati puoi contattarci telefonicamente ai
                recapiti sotto riportati.
            </p>
        </>
    );
}

function PrivacyContent() {
    return (
        <>
            <h2 className="font-display text-3xl text-ink mt-10">
                1. Titolare del trattamento
            </h2>
            <p>
                Il titolare del trattamento dei dati personali è{" "}
                <strong>Spazio Isa · Centro Estetico</strong>, con sede in Via
                A. Minghi 30 · 57124 Livorno (quartiere Coteto).
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                2. Dati raccolti
            </h2>
            <p>
                Questo sito non contiene form di contatto, aree riservate né
                iscrizioni a newsletter. Non raccogliamo dati personali
                attraverso il sito. La comunicazione con Spazio Isa avviene
                esclusivamente tramite telefono ai recapiti pubblicati.
            </p>
            <p>
                Se scegli di chiamarci o scriverci al di fuori del sito, i tuoi
                dati (nome, cognome, numero, richiesta) verranno trattati solo
                per rispondere alla tua richiesta e organizzare l'appuntamento.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                3. Finalità e base giuridica
            </h2>
            <p>
                I dati eventualmente comunicati per telefono o di persona sono
                trattati esclusivamente per la gestione degli appuntamenti,
                delle prenotazioni e delle consulenze estetiche. La base
                giuridica è il consenso dell'interessato e la necessità di
                erogare il servizio richiesto.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                4. Modalità di trattamento
            </h2>
            <p>
                I dati vengono trattati con strumenti manuali e informatici, con
                logiche strettamente correlate alle finalità e comunque in modo
                da garantirne la sicurezza e la riservatezza. Non vengono
                comunicati a terzi né diffusi.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                5. Conservazione dei dati
            </h2>
            <p>
                I dati vengono conservati per il tempo strettamente necessario
                alle finalità del trattamento, e comunque nel rispetto degli
                obblighi di legge.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                6. Diritti dell'interessato
            </h2>
            <p>
                In qualità di interessato, l'utente ha il diritto di chiedere al
                titolare l'accesso, la rettifica o la cancellazione dei propri
                dati personali, la limitazione o l'opposizione al loro
                trattamento, oltre al diritto alla portabilità dei dati (art.
                15 e seguenti del Regolamento UE 2016/679 – GDPR).
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                7. Reclamo
            </h2>
            <p>
                L'interessato ha inoltre il diritto di proporre reclamo al
                Garante per la Protezione dei Dati Personali.
            </p>

            <h2 className="font-display text-3xl text-ink mt-10">
                8. Modifiche
            </h2>
            <p>
                La presente informativa può essere aggiornata nel tempo, anche
                in relazione a modifiche normative. Ti invitiamo a consultare
                periodicamente questa pagina.
            </p>
        </>
    );
}
